import Vue from 'vue'
import moment from 'moment'
import Highcharts from 'highcharts'

import Utils from '@/providers/Utils'
import ChartDog from "@/components/ChartDog"

function getDataPointsForSeriesAndPhase(data, seriesIndex, phaseIndex) {
    return _.filter(data.chart.dataPoints, function (dp) {
        return dp.chartSeries == seriesIndex && dp.chartPhase == phaseIndex
    });
}

function getAimlinePointsForPhase(data, phaseIndex) {
    return _.filter(data.chart.aimlinePoints, function (dp) {
        return dp.chartPhase == phaseIndex
    });
}

function getAimlinePointsForSeries(data, seriesIndex) {
    return _.filter(data.chart.aimlinePoints, function (dp) {
        return dp.chartSeries == seriesIndex
    });
}

function getAimlinePointsForSeriesAndPhase(data, seriesIndex, phaseIndex) {
    return _.filter(data.chart.aimlinePoints, function (dp) {
        return dp.chartSeries == seriesIndex && dp.chartPhase == phaseIndex;
    })
}

function getPhaseByIndex(data, phaseIndex) {
    return _.find(data.chart.phases, function (p) {
        return p.index == phaseIndex
    });
}

function getSeriesByIndex(data, seriesIndex) {
    return _.find(data.chart.chartSeries, function (s) {
        return s.index == seriesIndex
    });
}

function getDataPointsForPhase(data, phaseIndex) {
    if (data) {
        return _.filter(data.chart.dataPoints, function (dp) {
            return dp.chartPhase == phaseIndex
        })
    } else {
        return []
    }
}

function getDataPointsForSeries(data, seriesIndex) {
    if (data) {
        return _.filter(data.chart.dataPoints, function (dp) {
            return dp.chartSeries == seriesIndex
        });
    } else {
        return []
    }
}

function getTrendStats(dps, phase) {
    var s = {}
    s.meanX = _.meanBy(dps, function (dp) {
        return moment(dp.date).valueOf()
    })
    s.meanXDays = _.meanBy(dps, function (dp) {
        return moment(dp.date).valueOf() / (24 * 60 * 60 * 1000)
    })
    s.meanY = _.meanBy(dps, function (dp) {
        return dp.value
    })
    var num = _.sumBy(dps, function (dp) {
        return (dp.value - s.meanY) * (moment(dp.date).valueOf() - s.meanX)
    })
    var denom = _.sumBy(dps, function (dp) {
        return Math.pow(moment(dp.date).valueOf() - s.meanX, 2)
    })
    s.m = num / denom
    s.mDays = s.m * 24 * 60 * 60 * 1000 // slope in units/day instead of units/millisecond
    s.b = s.meanY - s.m * s.meanX
    s.first = _.first(dps)
    s.last = _.last(dps)
    s.trendPoints = []
    if (s.first && s.last && s.first != s.last) {
        s.trendPoints.push([
            moment(phase.start).valueOf(),
            s.m * moment(phase.start).valueOf() + s.b
        ])
        s.trendPoints.push([
            moment(phase.end).add(-1, 'days').valueOf(),
            s.m * moment(phase.end).add(-1, 'days').valueOf() + s.b
        ])
        s.intercept = s.trendPoints[0][1]
    }

    return s
}

function buildMeanSeries(chartSeries, data) {
    var series = {
        name: chartSeries.title + ' (Mean)',
        color: chartSeries.color.lightCode,
        marker: {enabled: false},
        dashStyle: 'Solid',
        data: [],
    }

    _.each(data.chart.phases, function (phase) {
        var dps = getDataPointsForSeriesAndPhase(data, chartSeries.index, phase.index)
        var meanY = _.meanBy(dps, function (dp) {
            return dp.value
        })

        series.data.push([moment(phase.start).valueOf(), meanY])
        series.data.push([moment(phase.end).add(-1, 'days').valueOf(), meanY])

        // insert a null value between phases to break up trend plots
        series.data.push([moment(phase.end).add(-1, 'days').valueOf(), null])
    })
    return series
}


const debouncedUpdateChart = _.debounce(function (msg) {
    doUpdateChart(msg)
}, 500)

//  ----------------------------------------------------------------------------

export default {

    // -------- tool options ----------

    toolName: 'chartDog',
    label: 'ChartDog',
    logo: 'ChartDog-logo-32.png',

    getDefaultData() {
        return {
            docTitle: 'Untitled',
            currentStep: 1,
            currentSeries: 1,
            comments: '',
            showCommentsInReport: false,
            fontFace: 'sans',
            fontSize: 'small',
            chart: {
                chartTitle: 'My Chart',
                xAxisLabel: '',
                yAxisLabel: '',
                yAxisMin: null,
                yAxisMax: null,
                // NB: we call this 'chartSeries' to avoid confusion with 'series', used by highcharts
                chartSeries: [{
                    index: 1,
                    title: 'Series 1',
                    color: this.seriesColors[0],
                    marker: this.seriesMarkers[0],
                    size: 'S', // S/M/L
                    showTrendline: false,
                    showMean: false,
                    showAimline: false
                }],
                phases: [{
                    index: 1,
                    title: 'Phase 1',
                    start: moment(Utils.noonToday()).format('YYYY-MM-DD'),
                    end: moment(Utils.noonToday()).add(7, 'days').format('YYYY-MM-DD'),
                }],
                dataPoints: [],
                aimlinePoints: [{
                    type: 'start',
                    chartSeries: 1,
                    chartPhase: 1,
                    value: null
                }, {
                    type: 'end',
                    chartSeries: 1,
                    chartPhase: 1,
                    value: null
                }]
            }
        }
    },

    steps: [
        {id: 1, title: 'Define Series'},
        {id: 2, title: 'Enter Data'},
        {id: 3, title: 'Data Analysis'},
        {id: 4, title: 'Comments'},
        // { id: 5, title: 'Download Report' },
    ],

    seriesColors: [
        {label: 'Blue', code: '#005b89', lightCode: '#0076AF'},
        {label: 'Red', code: '#c73865', lightCode: '#DF5C86'},
        {label: 'Green', code: '#628C11', lightCode: '#7EAB29'},
        {label: 'Purple', code: '#B0407D', lightCode: '#CC679E'},
        {label: 'Orange', code: '#ea8e12', lightCode: '#F4A944'},
        {label: 'Yellow', code: '#8F6B00', lightCode: '#B78B07'},
        {label: 'Pumpkin', code: '#8F2900', lightCode: '#EA4F12'},
        {label: 'Indigo', code: '#171780', lightCode: '#4242AB'},
    ],

    seriesMarkers: [
        {label: 'Circle', name: 'circle'},
        {label: 'Square', name: 'square'},
        {label: 'Triangle', name: 'triangle'},
        {label: 'Diamond', name: 'diamond'},
    ],

    // onDataLoading: function (data) {
    //     // // convert saved dates from strings to Date objects, to allow binding by date inputs
    //     _.each(data.chart.phases, function (phase) {
    //         if (phase.start) {
    //             phase.start = new Date(phase.start);
    //         }
    //         if (phase.end) {
    //             phase.end = new Date(phase.end);
    //         }
    //     });
    //
    //     _.each(data.chart.dataPoints, function (dp) {
    //         if (dp.date) {
    //             dp.date = new Date(dp.date);
    //         }
    //     });
    // },

    // define which download methods are supported - see ToolContainerPreviewToolbar::inject
    provide: function () {
        return {
            downloadDocFile: null,
            downloadPdfFile: null,
            exportToGoogleForms: null,
        }
    },

    created() {
        this.updateChart = _.debounce(this.doUpdateChart, 500)
        this.updateChart()
    },

    data() {
        return {
            editingPhase: {},
            chartConfig: {},
            exportSummary: {phaseDataTables: []},
            plotBandColors: [
                '#c8ff9b',
                '#ffc89b',
                '#ffff9b',
                '#ffc8ff',
            ],
            chartDogSizes: [
                {id: 'S', lineWidth: 2, markerRadius: 4},
                {id: 'M', lineWidth: 4, markerRadius: 6},
                {id: 'L', lineWidth: 6, markerRadius: 8},
            ]
        }
    },
    computed: {
        currentChartSeries() {
            const data = this.docData
            if (data) {
                // console.log(data.currentSeries, data.chart.chartSeries);
                return _.find(data.chart.chartSeries, function (s) {
                    return s.index == data.currentSeries
                })
            } else {
                return null
            }
        },
        currentChartSeriesMarkerName() {
            const series = this.currentChartSeries
            if (series) {
                return series.marker.name
            } else {
                return ''
            }
        },
        currentChartSeriesColorCode() {
            const series = this.currentChartSeries
            if (series) {
                return series.color.code
            } else {
                return ''
            }
        },
        currentSeriesTab: {
            get() {
                return this.docData.currentSeries
            },
            set(val) {
                this.docData.currentSeries = val
            }
        }
    },
    methods: {

        chartTest(chart) {
            // debugger;
            console.log(chart)
            // console.log(chart.plotBorder)
            // console.log(chart.getSVG(Highcharts.chart(chart.userOptions)))
            // console.log(Highcharts.Chart)
        },

        seriesTabChanged(tabIndex) {
            console.log('tabIndex', tabIndex)
            this.docData.currentSeries = tabIndex
        },

        doUpdateChart(msg) {
            const data = this.docData
            if (data.chart) {
                let options = this.getChartOptions(data)
                this.setXAxisLimits(options)
                this.setYAxisLimits(options)
                Vue.set(this, 'chartConfig', options)
                this.updateExportSummary()
            }
            // self.ui.save(self);
            // $loading.finish('load-mask');
            if (msg) {
                alert(msg)
            }
        },

        updateExportSummary() {
            const data = this.docData
            this.exportSummary.phaseDataTables = []
            if (data) {
                var pointsByPhase = _.groupBy(data.chart.dataPoints, 'chartPhase')
                _.each(_.keys(pointsByPhase), (phaseIndex) => {
                    var phaseRows = []
                    var pointsByDate = _.groupBy(pointsByPhase[phaseIndex], 'date')
                    _.each(_.keys(pointsByDate), function (date) {
                        var pointsForDate = pointsByDate[date]
                        var phaseRow = {date: moment(date).valueOf(), seriesValues: []}
                        _.each(data.chart.chartSeries, function (series) {
                            var seriesPoint = _.find(pointsForDate, function (p) {
                                return p.chartSeries == series.index
                            });
                            phaseRow.seriesValues.push(seriesPoint ? seriesPoint.value : null)
                        });
                        phaseRows.push(phaseRow)
                    });
                    this.exportSummary.phaseDataTables.push({
                        phase: getPhaseByIndex(data, phaseIndex),
                        phaseRows: phaseRows
                    })
                })
            }

            // calculate page numbers
            this.exportSummary.pageTotal = 2
            var showTrends = (_.find(data.chart.chartSeries, function (series) {
                return series.showTrendline
            }) != null)
            var showMeans = (_.find(data.chart.chartSeries, function (series) {
                return series.showMean
            }) != null)
            if (showTrends && showMeans) {
                this.exportSummary.trendPageNumber = 3
                this.exportSummary.meanPageNumber = 4
                this.exportSummary.pageTotal = 4
            } else if (showTrends) {
                this.exportSummary.trendPageNumber = 3
                this.exportSummary.meanPageNumber = null
                this.exportSummary.pageTotal = 3
            } else if (showMeans) {
                this.exportSummary.trendPageNumber = null
                this.exportSummary.meanPageNumber = 3
                this.exportSummary.pageTotal = 3
            }
        },

        setXAxisLimits(options) {
            var minBandFrom = undefined
            var maxBandTo = undefined
            if (options.xAxis.plotBands.length) {
                var minBand = _.minBy(options.xAxis.plotBands, function (band) {
                    return band.from
                })
                var maxBand = _.maxBy(options.xAxis.plotBands, function (band) {
                    return band.to
                })
                if (minBand) {
                    minBandFrom = minBand.from
                }
                if (maxBand) {
                    maxBandTo = maxBand.to
                }
            }

            var minPointDate = undefined
            var maxPointDate = undefined
            if (options.series[0] && options.series[0].data.length) {
                var minPoint = _.minBy(options.series[0].data, function (dp) {
                    return dp[0]
                })
                var maxPoint = _.maxBy(options.series[0].data, function (dp) {
                    return dp[0]
                })
                minPointDate = minPoint[0]
                maxPointDate = maxPoint[0]
            }
            var min = _.min([minBandFrom, minPointDate, options.xAxis.min])
            var max = _.max([maxBandTo, maxPointDate, options.xAxis.max])
            options.xAxis.min = moment(min).add(-1, 'days').valueOf()
            options.xAxis.max = moment(max).add(1, 'days').valueOf()
        },

        setYAxisLimits(options) {
            const data = this.docData
            if (data.chart.yAxisMin) {
                options.yAxis.min = data.chart.yAxisMin
            }
            if (data.chart.yAxisMax) {
                options.yAxis.max = data.chart.yAxisMax
                options.yAxis.tickInterval = 1
            }
        },

        setSeriesColor(series, color) {
            series.color = color
        },
        setSeriesMarker(series, marker) {
            series.marker = marker
        },

        showPhaseEditModal(phase) {
            this.editingPhase = phase
            this.$bvModal.show('edit-phase-modal')
        },

        hidePhaseEditModal() {
            this.$bvModal.hide('edit-phase-modal')
        },

        // returns array of data points
        listDataPointsForSeriesAndPhase(seriesIndex, phaseIndex) {
            const data = this.docData
            if (data) {
                var dps = getDataPointsForSeriesAndPhase(data, seriesIndex, phaseIndex)
                return dps
            } else {
                return []
            }
        },

        // returns a string of values, joined by commas
        listDataPointValuesForSeriesAndPhase(seriesIndex, phaseIndex) {
            const data = this.docData
            if (data) {
                var dps = getDataPointsForSeriesAndPhase(data, seriesIndex, phaseIndex)
                return _.map(dps, 'value').join(', ')
            } else {
                return ''
            }
        },

        hasDuplicateDate(dp) {
            const data = this.docData
            var dupe = _.find(data.chart.dataPoints, function (dp2) {
                return dp.date && dp2.date && dp2 != dp && dp2.chartSeries == dp.chartSeries && moment(dp2.date).isSame(dp.date, 'day')
            })
            return dupe != null
        },

        deleteDataPoint(dp) {
            const data = this.docData
            data.chart.dataPoints = _.without(data.chart.dataPoints, dp)
        },

        addDataPoint(phaseIndex) {
            const data = this.docData
            const phase = getPhaseByIndex(data, phaseIndex)
            let date = moment().toDate()

            const points = getDataPointsForSeriesAndPhase(data, data.currentSeries, phaseIndex)
            if (points.length) {
                var maxPoint = _.maxBy(points, function (dp) {
                    return dp.date
                });
                date = moment(maxPoint.date).add(1, 'days').toDate()
            } else if (phase && phase.start) {
                date = moment(phase.start).toDate()
            }

            const dp = {
                chartSeries: data.currentSeries,
                chartPhase: phaseIndex,
                date: moment(date).format('YYYY-MM-DD'),
                value: 0
            };
            if (!data.chart.dataPoints) {
                data.chart.dataPoints = []
            }
            data.chart.dataPoints.push(dp)

            this.dataPointDateChanged(dp)
        },

        getChartOptions(data) {
            return {
                chart: {
                    renderTo: 'chart-container',
                    events: {
                        click: function (e) {
                            console.log(
                                'chart click',
                                Highcharts.dateFormat('%Y-%m-%d %H:%M:%S', e.xAxis[0].value),
                                e.yAxis[0].value
                            )
                        }
                    }
                },
                title: {text: data.chart.chartTitle},
                xAxis: {
                    type: 'datetime',
                    title: {
                        text: data.chart.xAxisLabel
                    },
                    labels: {
                        formatter: function () {
                            if (this.isFirst || this.isLast) {
                                return Highcharts.dateFormat('%m/%e/%y', this.value);
                            } else {
                                return Highcharts.dateFormat('%m/%e', this.value);
                            }
                        }
                    },
                    plotBands: this.getPlotBands(data)
                },
                yAxis: {
                    title: {
                        text: data.chart.yAxisLabel
                    },
                    labels: {
                        enabled: true
                    },
                },
                legend: {
                    enabled: true
                },
                series: this.getSeriesData(data)
            }
        },

        getPlotBands(data) {
            var bands = []
            _.each(data.chart.phases, (phase, index) => {
                var points = getDataPointsForPhase(data, phase.index)
                bands.push({
                    label: {text: phase.title},
                    color: this.plotBandColors[index],
                    borderColor: '#999',
                    borderWidth: 1,
                    from: moment(phase.start).valueOf(),
                    to: moment(phase.end).add(0, 'days').valueOf()
                });
            })
            return bands
        },
        getSeriesData(data) {
            var seriesList = []
            _.each(data.chart.chartSeries, (cs) => {
                seriesList.push(this.buildDataPointSeries(cs, data))
                if (cs.showTrendline) {
                    seriesList.push(this.buildTrendlineSeries(cs, data))
                }
                if (cs.showMean) {
                    seriesList.push(buildMeanSeries(cs, data))
                }

                if (cs.showAimline) {
                    var aps = this.buildAimlinePointSeries(cs, data)
                    if (aps.data.length) {
                        seriesList.push(aps)
                    }
                }
            });

            return seriesList
        },
        buildDataPointSeries(chartSeries, data) {
            var size = _.find(this.chartDogSizes, function (size) {
                return size.id == chartSeries.size
            });
            var series = {
                name: chartSeries.title,
                color: chartSeries.color.code,
                marker: {
                    label: chartSeries.marker.label,
                    name: chartSeries.marker.name
                },
                lineWidth: (size ? size.lineWidth : 2),
                enableMouseTracking: false,
                data: [],
            }
            _.each(data.chart.phases, function (phase) {
                var points = getDataPointsForSeriesAndPhase(data, chartSeries.index, phase.index)
                if (points.length) {
                    _.each(points, function (dp) {
                        series.data.push([new Date(dp.date).getTime(), _.toNumber(dp.value)])
                    });
                    // insert a null value between phases to break up plots
                    series.data.push([new Date(_.last(points).date).getTime(), null])
                }
            })
            return series
        },

        buildAimlinePointSeries(chartSeries, data) {
            var series = {
                name: chartSeries.title + ' (Aimline)',
                color: chartSeries.color.lightCode,
                marker: {enabled: false},
                dashStyle: 'ShortDash',
                data: [],
            }
            _.each(data.chart.phases, function (phase) {

                var aimlinePoints = getAimlinePointsForSeriesAndPhase(data, chartSeries.index, phase.index)
                var apsWithDates = _.map(aimlinePoints, function (ap) {
                    if (ap.type == 'start') {
                        return [ap, new Date(phase.start).getTime()]
                    } else {
                        // phase end will be one day beyond last data point in phase, so move aimline end point back a day
                        return [ap, moment(phase.end).add(-1, 'days').valueOf()]
                    }
                })
                if (apsWithDates.length) {
                    _.each(apsWithDates, function (apd) {
                        if (apd[0].value) {
                            series.data.push([new Date(apd[1]).getTime(), _.toNumber(apd[0].value)])
                        }
                    });
                    // insert a null value between phases to break up aimline plots
                    series.data.push([new Date(_.last(apsWithDates)[1]).getTime(), null])
                }
            })
            return series
        },
        buildTrendlineSeries(chartSeries, data) {
            var series = {
                name: chartSeries.title + ' (Trend)',
                color: chartSeries.color.lightCode,
                marker: {enabled: false},
                dashStyle: 'Dot',
                data: [],
            }
            _.each(data.chart.phases, function (phase) {
                var dps = getDataPointsForSeriesAndPhase(data, chartSeries.index, phase.index)
                var stats = getTrendStats(dps, phase)
                if (stats.trendPoints.length == 2) {
                    series.data.push(stats.trendPoints[0])
                    series.data.push(stats.trendPoints[1])
                    // insert a null value between phases to break up trend plots
                    series.data.push([stats.trendPoints[0][0], null])
                }
            })
            return series
        },
        getTrendlineDescription(chartSeries, phase) {
            const data = this.docData
            var dps = getDataPointsForSeriesAndPhase(data, chartSeries.index, phase.index)
            var stats = getTrendStats(dps, phase)
            var desc = {body: '', notes: ''}
            if (stats.trendPoints.length == 2) {
                var valDelta = stats.trendPoints[1][1] - stats.trendPoints[0][1]
                var diff = moment.duration(
                    moment(stats.trendPoints[1][0]).diff(
                        moment(stats.trendPoints[0][0])))
                var days = diff.asDays()
                var perDay = parseFloat(Math.abs(valDelta / days)).toFixed(2)
                var perWeek = parseFloat(perDay * 7).toFixed(2)
                var slope = parseFloat(stats.mDays).toFixed(2)
                var intercept = parseFloat(stats.intercept).toFixed(2)
                desc.body = "The trend shows an estimated " + (valDelta > 0 ? "INCREASE" : "DECREASE") +
                    " of " + perDay + " units per calendar day and " + perWeek + " units per calendar week " +
                    "(Slope = " + slope + ", Intercept = " + intercept + ")."
                if (dps.length < 6) {
                    desc.notes = "NOTE: Interpret this trendline with caution, as the data series contains fewer than 6 data points."
                }
            }
            return desc
        },
        getMeanDescription(chartSeries, phase) {
            const data = this.docData
            var dps = getDataPointsForSeriesAndPhase(data, chartSeries.index, phase.index)
            var stats = getTrendStats(dps, phase)
            var desc = {body: '', notes: ''}
            desc.body = "Contains " + dps.length + " data points, whose mean value equals " + parseFloat(stats.meanY).toFixed(1)
            return desc
        },

        deleteChartSeries(series) {
            const data = this.docData
            if (confirm('Delete series ' + series.title + ', and all data points in this series?')) {
                var points = getDataPointsForSeries(data, series.index)
                _.each(points, function (dp) {
                    data.chart.dataPoints = _.without(data.chart.dataPoints, dp)
                });

                var aimlinePoints = getAimlinePointsForSeries(data, series.index)
                _.each(aimlinePoints, function (ap) {
                    data.chart.aimlinePoints = _.without(data.chart.aimlinePoints, ap)
                });

                data.chart.chartSeries = _.without(data.chart.chartSeries, series)
            }
        },

        addChartSeries() {
            const data = this.docData
            var maxSeries = _.maxBy(data.chart.chartSeries, function (s) {
                return s.index
            });
            var newIndex = (maxSeries ? maxSeries.index + 1 : 1)
            data.chart.chartSeries.push({
                index: newIndex,
                title: 'Series ' + newIndex,
                color: this.tool.options.seriesColors[newIndex],
                marker: this.tool.options.seriesMarkers[newIndex],
                size: 'S',
                showTrendline: false,
                showMean: false,
                showAimline: false
            })
            data.currentSeries = newIndex
            this.addMissingAimlinePoints()
        },
        deletePhase(phase) {
            const data = this.docData
            if (confirm('Delete phase ' + phase.title + ', and all data points in this phase?')) {
                var points = getDataPointsForPhase(data, phase.index)
                _.each(points, function (dp) {
                    data.chart.dataPoints = _.without(data.chart.dataPoints, dp)
                })

                var aimlinePoints = getAimlinePointsForPhase(data, phase.index)
                _.each(aimlinePoints, function (ap) {
                    data.chart.aimlinePoints = _.without(data.chart.aimlinePoints, ap)
                })

                data.chart.phases = _.without(data.chart.phases, phase)
                this.adjustPhases()
            }
        },
        addPhase() {
            const data = this.docData

            var maxIndexPhase = _.maxBy(data.chart.phases, function (p) {
                return p.index
            });
            var newIndex = (maxIndexPhase ? maxIndexPhase.index + 1 : 1)

            var start = moment().toDate()
            var end = moment().add(7, 'days').toDate()

            var maxEndPhase = _.maxBy(data.chart.phases, function (phase) {
                return phase.end
            })
            if (maxEndPhase) {
                start = moment(maxEndPhase.end).add(1, 'days').format('YYYY-MM-DD')
                end = moment(maxEndPhase.end).add(7, 'days').format('YYYY-MM-DD')
            }

            data.chart.phases.push({
                index: newIndex,
                title: 'Phase ' + newIndex,
                start: start,
                end: end
            })

            this.addMissingAimlinePoints()
            this.adjustPhases()
        },
        // ensure that start/end aimline points exist for every series/phase combination
        addMissingAimlinePoints() {
            const data = this.docData

            _.each(data.chart.chartSeries, function (series) {
                _.each(data.chart.phases, function (phase) {
                    var aps = getAimlinePointsForSeriesAndPhase(data, series.index, phase.index)
                    if (!aps.length) {
                        var ap1 = {
                            type: 'start',
                            chartSeries: series.index,
                            chartPhase: phase.index,
                            value: null
                        }
                        var ap2 = {
                            type: 'end',
                            chartSeries: series.index,
                            chartPhase: phase.index,
                            value: null
                        }
                        data.chart.aimlinePoints.push(ap1)
                        data.chart.aimlinePoints.push(ap2)
                    }
                })
            })
        },
        dataPointDateChanged(dp) {
            if (!dp.date) {
                this.deleteDataPoint(dp) // having a null date value screws up phase display, so just delete the dp
            }
            const data = this.docData
            var dataPoints = data.chart.dataPoints
            data.chart.dataPoints = _.sortBy(dataPoints, function (dp) {
                return dp.date
            });
            var msg = this.adjustPointPhases()
            this.adjustPhases()
        },
        pointIsWithinPhase(dp, phase) {
            const data = this.docData
            var phasePoints = getDataPointsForPhase(data, phase.index)

            if (phasePoints.length == 0) {
                // point is between phase start/end
                return (dp.date.valueOf() >= phase.start.valueOf() &&
                    dp.date.valueOf() <= phase.end.valueOf())
            }
            if (phasePoints.length == 1) {
                // point is after first point of phase and before phase end
                return (dp.date.valueOf() >= _.first(phasePoints).date.valueOf() &&
                    dp.date.valueOf() <= phase.end.valueOf())
            } else if (phasePoints.length > 1) {
                // point is between first and last points of phase
                return (dp.date.valueOf() >= _.first(phasePoints).date.valueOf() &&
                    dp.date.valueOf() <= _.last(phasePoints).date.valueOf())
            }
        },
        // possibly move points between phases
        adjustPointPhases() {
            const data = this.docData

            // if a data point falls within the data point range for a different phase, move it to that phase
            var newPhase = null
            _.each(data.chart.dataPoints, (dp) => {
                var phase = getPhaseByIndex(data, dp.chartPhase)
                if (dp.date && (dp.date.valueOf() < phase.start || dp.date.valueOf() > phase.end)) {
                    _.each(data.chart.phases, (phase) => {
                        if (phase.index != dp.chartPhase) {
                            if (this.pointIsWithinPhase(dp, phase)) {
                                dp.chartPhase = phase.index
                                newPhase = phase
                            }
                        }
                    })
                }
            })

            // if a data point falls after all phases and is not already in the last phase, move it to the last phase
            _.each(data.chart.dataPoints, function (dp) {
                var maxEndPhase = _.maxBy(data.chart.phases, function (phase) {
                    return phase.end
                });
                if (maxEndPhase && maxEndPhase.index != dp.chartPhase && dp.date > maxEndPhase.end) {
                    dp.chartPhase = maxEndPhase.index
                    newPhase = maxEndPhase
                }
            })

            // if a data point falls before all phases and is not already in the first phase, move it to the first phase
            _.each(data.chart.dataPoints, function (dp) {
                var minStartPhase = _.minBy(data.chart.phases, function (phase) {
                    return phase.start
                });
                if (minStartPhase && minStartPhase.index != dp.chartPhase && dp.date < minStartPhase.start) {
                    dp.chartPhase = minStartPhase.index
                    newPhase = minStartPhase
                }
            })

            if (newPhase) {
                return 'This point will be moved to ' + newPhase.title + ', because it falls in that date range.'
            } else {
                return null
            }
        },
        // possibly adjust phase start/end dates
        // NB: editing of phase start/end dates is limited (in modal) to ranges based on neighboring phases,
        // so any adjustments required here should arise from movement of points, not editing of start/end dates
        adjustPhases() {
            const data = this.docData

            // if a phase has at least one data point, set the phase start to the date of the first point
            _.each(data.chart.phases, function (phase) {
                var firstPoint = _.first(getDataPointsForPhase(data, phase.index))
                if (firstPoint) {
                    phase.start = firstPoint.date;
                }
            })

            // sort phases by start date
            data.chart.phases = _.sortBy(data.chart.phases, function (p) {
                return p.start
            });

            // TODO: check phase boundary when moving point to 1 day before next phase start

            // set each phase's end date based on last point, entered end date, and overlapping next phase
            _.each(data.chart.phases, (phase) => {
                var lastPoint = _.last(getDataPointsForPhase(data, phase.index))
                var maxEnd = this.getMaxEndDate(phase)
                // BUG: phase.end will sometimes be set to undefined if you select the last date before
                // a disabled date in the picker (??). This should only happen if there is a next phase,
                // in which case maxEnd will be non-null...
                if (!phase.end && maxEnd) {
                    phase.end = new Date(maxEnd)
                }
                if (lastPoint) {
                    // use last point date or entered end date, whichever is later
                    phase.end = _.max([
                        moment(lastPoint.date).add(1, 'days').toDate(),
                        phase.end
                    ])
                    // if next phase's start date falls before this phase's end date, move end date back
                    if (maxEnd != "") {
                        phase.end = _.min([
                            phase.end,
                            new Date(maxEnd)
                        ])
                    }
                }
            });
        },
        getMinStartDate(phase) {
            const data = this.docData
            var pos = data.chart.phases.indexOf(phase)
            if (pos > 0) {
                var prevPhaseEnd = data.chart.phases[pos - 1].end
                return moment(prevPhaseEnd).add(1, 'days').format('YYYY-MM-DD')
            } else {
                return ""
            }
        },

        getMaxEndDate(phase) {
            const data = this.docData
            var pos = data.chart.phases.indexOf(phase)
            if (pos < data.chart.phases.length - 1) {
                var nextPhaseStart = data.chart.phases[pos + 1].start
                return moment(nextPhaseStart).add(0, 'days').format('YYYY-MM-DD')
            } else {
                return ""
            }
        },

    },
    watch: {
        '$store.state.doc.data.chart': {
            deep: true,
            handler(newVal, oldVal) {
                console.log('watcher updateChart')
                this.updateChart()
            }
        }
    }

}
